import { useMonthlyCharges } from '../../lib/api/hooks-subscriptions.js';
import { Empty, ErrorBanner, CardHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatMoney, formatUnitPrice } from '../../lib/format.js';
import { docDate } from './InvoiceDocument.js';

/**
 * What an account is billed each month (docs/28 B3): the current period so far, as
 * the run on its billing day would draft it if nothing changes before then. Seats
 * at the most in use on any day, numbers and a call plan by the day.
 */
export function MonthlyChargesCard({ tenantId, staff }: { tenantId: string; staff: boolean }) {
  const charges = useMonthlyCharges(tenantId);
  const m = charges.data;

  return (
    <div className="arc-card" style={{ marginTop: 16 }}>
      <CardHead title="Monthly charges"
        sub={m?.billed_by === 'platform'
          ? `${docDate(m.period_start)} to ${docDate(m.period_end)}, invoiced ${docDate(m.billing_date)}`
          : undefined} />

      {charges.error ? (
        <div className="arc-card-body"><ErrorBanner error={charges.error} /></div>
      ) : !m ? (
        <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 60 }} /></div>
      ) : m.billed_by === 'reseller' ? (
        <div className="arc-card-body arc-muted">Billed by this account&apos;s reseller.</div>
      ) : (
        <>
          {staff && m.untyped_seats > 0 && (
            <div className="arc-card-body" style={{ paddingBottom: 0 }}>
              <div className="arc-banner arc-banner-warn" role="alert">
                <Icon name="warning" size={14} />
                <span>
                  {m.untyped_seats === 1
                    ? '1 seat has no extension type, so it is not billed.'
                    : `${m.untyped_seats} seats have no extension type, so they are not billed.`}
                </span>
              </div>
            </div>
          )}
          {m.lines.length === 0 ? (
            <Empty icon="billing" title="No monthly charges" hint="Priced seats, numbers and call plans appear here." />
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="arc-td-right">Quantity</th>
                    <th className="arc-td-right">Unit price</th>
                    <th className="arc-td-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {m.lines.map((l, n) => (
                    <tr key={n}>
                      <td>{l.description}</td>
                      <td className="arc-td-right arc-num">{l.quantity}</td>
                      <td className="arc-td-right arc-num">{formatUnitPrice(l.unit_price, m.currency)}</td>
                      <td className="arc-td-right arc-num">{formatMoney(l.amount, m.currency)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} className="arc-td-right arc-muted">Subtotal, excluding VAT</td>
                    <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>
                      {formatMoney(m.subtotal, m.currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
